// ============================================================
// TMR Portal Admin — aba Taxas dentro do detalhe da empresa (08)
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn, StatusBadge } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Notice } from '../../components/Notice';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Segmented } from '../../components/primitives';
import { SelectField } from '../../components/fields';
import { Icons, StrokeIcon } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import {
  companyRateExportPage,
  useCompanyRates,
  useDeleteRate,
  useEndTemporaryRate,
  useRateDrift,
  useSyncRates,
} from '../../data/rates/queries';
import { bandLabel, formatRateValue, syncSummary } from '../../data/rateFormat';
import { ExportMenu } from '../../components/ExportMenu';
import { COMPANY_RATE_EXPORT_COLUMNS } from './rateExportColumns';
import {
  SCOPE_LABEL,
  TEMPORARY_STATE_BADGE,
  TEMPORARY_STATE_LABEL,
  cascadeTargets,
  cascades,
  formatDate,
  isAwaitingLegacy,
  launchProducts,
  rateTitle,
} from '../../data/rates/status';
import type { CompanyRate, RateScope, RateSyncResult } from '../../data/rates/contract';
import { RateFormModal } from './RateFormModal';
import { RateHistoryModal } from './RateHistoryModal';
import { TemporaryRateModal } from './TemporaryRateModal';

/** Props for {@link CompanyRatesTab}. */
interface CompanyRatesTabProps {
  companyId: string;
  /**
   * Whether the creation modal is open. Controlled by the screen, because the button that opens it lives in
   * the screen header — one primary action per tab, in the same slot as "Editar cadastro".
   */
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
}

/** Which rates the tab is showing. `all` is the default because comparing bands is the point. */
type ScopeFilter = 'all' | RateScope;

/**
 * One Empresa's rates — the definitive management of what the TMR charges it (`08`).
 *
 * Three things about this screen are not cosmetic:
 *
 * - **It leads with the effective value.** The standard value is what the Empresa pays normally; the
 *   effective value is what it pays today. They differ whenever a temporary window is open, and the second
 *   is the one the legacy is calculating with.
 * - **It says when the legacy is out of step.** A rate reading 1,5% here while `taxas_produtor` still holds
 *   3,03% is a rate nobody is being charged, and the replication is asynchronous — so "aguardando sincronia"
 *   is a state the operator has to be able to see rather than infer.
 * - **It offers "Sincronizar com o legado".** The legacy keeps changing rates on its own — a coupon rewrites
 *   values in bulk, a new product copies the Empresa's rates into new rows — and this is the way back.
 */
export function CompanyRatesTab({ companyId, creating, onCreatingChange }: CompanyRatesTabProps) {
  const [scope, setScope] = useState<ScopeFilter>('all');
  // Empty means "every product". Kept as the legacy product id, which is what a rate carries.
  const [productId, setProductId] = useState<string>('');
  const [editing, setEditing] = useState<CompanyRate | null>(null);
  const [applyingTo, setApplyingTo] = useState<CompanyRate | null>(null);
  const [endingFor, setEndingFor] = useState<CompanyRate | null>(null);
  // Applying a window asks whether to cascade; ending one used to force it. The two have to agree —
  // otherwise "encerrar" silently moves rates the operator never chose to move.
  const [endCascade, setEndCascade] = useState(true);
  const [deleting, setDeleting] = useState<CompanyRate | null>(null);
  // Read on demand: most visits ask what a rate is, not how it got there (15 R4).
  const [historyFor, setHistoryFor] = useState<CompanyRate | null>(null);

  const canRead = usePermission(PERMISSIONS.companyRatesRead);
  const canManage = usePermission(PERMISSIONS.companyRatesManage);

  const query = useCompanyRates(canRead ? companyId : undefined);
  const sync = useSyncRates(companyId);
  const endTemporary = useEndTemporaryRate(companyId);
  const remove = useDeleteRate(companyId);

  // The rates of an Empresa are seeded into the legacy when the producer is replicated, never here — so the
  // first visit has nothing to show until they are fetched. The fetch is a write, so it goes through the
  // manage-gated sync rather than through the list (`15` §10, D-A2). Once per mount: a failure must not
  // become a loop, and the operator still has the button.
  const [syncResult, setSyncResult] = useState<RateSyncResult | null>(null);
  // Off until asked: it is a round trip to the legacy, and the tab is opened far more often than a value
  // is doubted.
  const [checkingDrift, setCheckingDrift] = useState(false);
  const drift = useRateDrift(companyId, checkingDrift);
  const autoSynced = useRef(false);
  const needsImport = query.data?.needsImport ?? false;
  useEffect(() => {
    if (!needsImport || !canManage || autoSynced.current) return;
    autoSynced.current = true;
    void sync.mutateAsync(undefined).then(setSyncResult).catch(() => undefined);
  }, [needsImport, canManage, sync]);

  // Without company.rates.read the list would 403 on every render. Saying so beats an error state that
  // reads like a fault — the server is right to refuse (06 §6).
  if (!canRead) {
    return (
      <div className="pa-card">
        <EmptyState
          message="Você não tem permissão para ver as taxas desta empresa."
          hint="Peça o acesso a quem administra os papéis."
        />
      </div>
    );
  }

  if (query.isPending) return <Skeleton rows={5} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const all = query.data.items;
  const launchCount = all.filter((rate) => rate.scope === 'Launch').length;
  const pendingSync = all.filter(isAwaitingLegacy).length;

  // Built from the rates on screen, not from a catalogue: only a product this Empresa actually has a rate
  // for can narrow anything, and the list already carries the name of each one.
  const products = launchProducts(all);

  // Filtering a product only makes sense among launch rates — an Empresa-scope rate belongs to no product.
  // So the picker hides under "Da empresa", and its choice is dropped rather than kept invisibly active.
  const productApplies = scope !== 'Standard' && products.length > 1;
  const effectiveProductId = productApplies ? productId : '';

  const shown = all
    .filter((rate) => scope === 'all' || rate.scope === scope)
    .filter((rate) => effectiveProductId === '' || String(rate.productId) === effectiveProductId);

  const newRateButton = canManage ? (
    <Btn variant="primary" icon={Icons.plus} onClick={() => onCreatingChange(true)}>
      Nova taxa
    </Btn>
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* An Empresa with no producer in the legacy has nowhere for a rate to be replicated to, and its
          rates were never seeded. Showing an empty list would read as "no rates", which is a different
          thing from "no destination yet". */}
      {!query.data.legacyLinked && (
        <Notice tone="warning">
          Esta empresa ainda não foi replicada para o sistema legado. As taxas dela nascem lá, na
          replicação — até isso acontecer, o que for cadastrado aqui fica aguardando.
        </Notice>
      )}

      {/* Nothing here yet, and nobody on this screen can fetch it. Saying "nenhuma taxa" would be false —
          the Empresa has rates, they just have not been brought over. */}
      {query.data.needsImport && !canManage && !sync.isPending && (
        <Notice tone="warning">
          As taxas desta empresa ainda não foram trazidas do sistema legado. Quem tem permissão para
          gerenciar taxas pode fazer isso pelo botão “Sincronizar com o legado”.
        </Notice>
      )}

      {query.data.needsImport && canManage && sync.isPending && (
        <Notice tone="neutral">Trazendo as taxas do sistema legado…</Notice>
      )}

      {/* What the reconciliation actually did. It was being discarded, so an operator who clicked
          "Sincronizar" and saw nothing change had no way to tell "nothing to bring" from "it did not run". */}
      {syncResult && !sync.isPending && <Notice tone="neutral">{syncSummary(syncResult)}</Notice>}

      {/* "Aguardando sincronia" is the V2 asking its own memory; this is the answer from the other side.
          A coupon, a back-office edit or an abandoned export all leave a row reading green while the clinic
          is charged something else (`15` §3 E). */}
      {checkingDrift && drift.data && !drift.isFetching && (
        <Notice tone={drift.data.items.length > 0 ? 'danger' : 'neutral'}>
          {!drift.data.legacyAvailable
            ? 'Não foi possível ler o sistema legado agora, então não dá para afirmar que os valores conferem.'
            : drift.data.items.length === 0
              ? `${drift.data.checked} ${drift.data.checked === 1 ? 'taxa confere' : 'taxas conferem'} com o sistema legado.`
              : `${drift.data.items.length} de ${drift.data.checked} taxas estão com valor diferente no sistema legado — é o valor de lá que a clínica paga. Sincronize para trazer, ou edite aqui para sobrescrever.`}
        </Notice>
      )}

      {pendingSync > 0 && (
        <Notice tone="warning">
          {pendingSync === 1
            ? '1 taxa ainda não chegou ao sistema legado — até chegar, a cobrança segue pelo valor anterior.'
            : `${pendingSync} taxas ainda não chegaram ao sistema legado — até chegarem, a cobrança segue pelos valores anteriores.`}
        </Notice>
      )}

      {sync.error && (
        <Notice tone="danger">
          {sync.error instanceof ApiError ? sync.error.detail : 'Não foi possível sincronizar as taxas.'}
        </Notice>
      )}

      {(endTemporary.error ?? remove.error) && (
        <Notice tone="danger">
          {(() => {
            const error = endTemporary.error ?? remove.error;
            return error instanceof ApiError ? error.detail : 'Não foi possível concluir a ação.';
          })()}
        </Notice>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        {/* The subfilter only earns its place once the Empresa actually has launch rates — otherwise it is
            two buttons where one of them can never return anything. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          {launchCount > 0 ? (
            <Segmented
              value={scope}
              onChange={setScope}
              options={[
                { value: 'all', label: `Todas (${all.length})` },
                { value: 'Standard', label: `Da empresa (${all.length - launchCount})` },
                { value: 'Launch', label: `Dos lançamentos (${launchCount})` },
              ]}
            />
          ) : (
            <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
              {all.length === 1 ? '1 taxa' : `${all.length} taxas`}
            </span>
          )}

          {/* Only earns its place with more than one product: with a single one it is a dropdown whose
              every option shows the same rows the scope filter already showed. */}
          {productApplies && (
            <div style={{ width: 220 }}>
              <SelectField
                label="Produto"
                value={productId}
                onChange={setProductId}
                options={products}
                placeholder="Todos"
              />
            </div>
          )}
        </div>

        {query.data.legacyLinked && (
          <Btn
            variant="ghost"
            icon={Icons.alert}
            onClick={() => {
              setCheckingDrift(true);
              void drift.refetch();
            }}
            disabled={drift.isFetching}
          >
            {drift.isFetching ? 'Conferindo…' : 'Conferir no legado'}
          </Btn>
        )}

        {canManage && query.data.legacyLinked && (
          <Btn
            variant="secondary"
            icon={Icons.swap}
            onClick={() => void sync.mutateAsync(undefined).then(setSyncResult)}
            disabled={sync.isPending}
          >
            {sync.isPending ? 'Sincronizando…' : 'Sincronizar com o legado'}
          </Btn>
        )}

        {/* Exports the whole rate table of this Empresa, not the filtered view: the filters here narrow a
            comparison the operator is making on screen, and a file of "só as do lançamento" is the one
            thing nobody takes to a negotiation. */}
        <ExportMenu
          spec={{
            fileName: `taxas-${companyId}`,
            columns: COMPANY_RATE_EXPORT_COLUMNS,
            fetchPage: companyRateExportPage(companyId),
          }}
          totalCount={all.length}
        />
      </div>

      {shown.length === 0 ? (
        <div className="pa-card">
          <EmptyState
            message={
              all.length === 0
                ? 'Esta empresa ainda não tem taxas.'
                : 'Nenhuma taxa neste filtro.'
            }
            hint={
              all.length === 0 && canManage
                ? 'As taxas iniciais vêm do sistema legado quando a empresa é replicada. Você também pode cadastrar uma aqui.'
                : undefined
            }
            action={all.length === 0 ? newRateButton : undefined}
          />
        </div>
      ) : (
        <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
          {shown.map((rate) => (
            <RateRow
              key={rate.id}
              rate={rate}
              canManage={canManage}
              onEdit={() => setEditing(rate)}
              onHistory={() => setHistoryFor(rate)}
              onApplyTemporary={() => setApplyingTo(rate)}
              onEndTemporary={() => setEndingFor(rate)}
              onDelete={() => setDeleting(rate)}
            />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <RateFormModal
          companyId={companyId}
          rate={editing}
          onClose={() => {
            setEditing(null);
            onCreatingChange(false);
          }}
        />
      )}

      {applyingTo && (
        <TemporaryRateModal
          companyId={companyId}
          rate={applyingTo}
          all={all}
          onClose={() => setApplyingTo(null)}
        />
      )}

      {endingFor && (
        <ConfirmDialog
          title="Encerrar taxa temporária?"
          message={
            <>
              A taxa volta para {formatRateValue(endingFor.standardValue, endingFor.rateTypeId)} a partir de
              hoje.
              {/* Applying a window asks whether to cascade, so ending one has to ask too. Forcing it here
                  moved launch rates the operator never chose to move — and the counts are the same ones
                  the apply modal shows, so the two screens agree on what "junto" means. */}
              {cascades(endingFor) && cascadeTargets(endingFor, all).length > 0 && (
                <label
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    marginTop: 12,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={endCascade}
                    onChange={(event) => setEndCascade(event.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    Encerrar também nas {cascadeTargets(endingFor, all).length} taxas de lançamento com o
                    mesmo tipo e faixa.
                  </span>
                </label>
              )}
            </>
          }
          confirmLabel="Encerrar"
          icon={Icons.clock}
          onConfirm={() =>
            endTemporary.mutateAsync({
              rateId: endingFor.id,
              request: { cascadeToLaunchRates: endCascade },
            })
          }
          onClose={() => {
            setEndingFor(null);
            setEndCascade(true);
          }}
        />
      )}

      {historyFor && (
        <RateHistoryModal
          companyId={companyId}
          rate={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Excluir esta taxa?"
          message={
            <>
              {rateTitle(deleting)} · {bandLabel(deleting)}. Ela também é removida do sistema legado.
              Pedidos já calculados mantêm o valor que tinham.
            </>
          }
          confirmLabel="Excluir taxa"
          danger
          icon={Icons.alert}
          onConfirm={() => remove.mutateAsync(deleting.id)}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

/** Props for {@link RateRow}. */
interface RateRowProps {
  rate: CompanyRate;
  canManage: boolean;
  onEdit: () => void;
  /** Opens the audit trail. Offered to anyone who may read rates — reading how it got here is a read. */
  onHistory: () => void;
  onApplyTemporary: () => void;
  onEndTemporary: () => void;
  onDelete: () => void;
}

/** One rate: what it is, what it costs today, and what is true about it that the operator can act on. */
function RateRow({ rate, canManage, onEdit, onHistory, onApplyTemporary, onEndTemporary, onDelete }: RateRowProps) {
  // Not `!== 'None'`: an Ended window is over. Treating it as live offered "Encerrar temporária" for
  // something already finished, and hid the only action that made sense — applying a new one.
  const hasWindow = rate.temporaryState === 'Active' || rate.temporaryState === 'Scheduled';
  const ended = rate.temporaryState === 'Ended';
  const active = rate.temporaryState === 'Active';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        padding: '14px 18px',
        borderBottom: '1px solid var(--border-subtle)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 240, flex: '1 1 320px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14.5, fontWeight: 550 }}>{rateTitle(rate)}</span>
          <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{SCOPE_LABEL[rate.scope]}</span>
          {(hasWindow || ended) && <StatusBadge status={TEMPORARY_STATE_BADGE[rate.temporaryState]} />}
        </div>

        <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 4 }}>
          {bandLabel(rate)}
          {rate.creditEngineTypeName ? ` · ${rate.creditEngineTypeName}` : ''}
          {rate.couponId != null ? ` · valor posto pelo cupom ${rate.couponId}` : ''}
        </div>

        {(hasWindow || ended) && rate.temporary && (
          <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', marginTop: 4 }}>
            {TEMPORARY_STATE_LABEL[rate.temporaryState]} · {formatDate(rate.temporary.startDate)} a{' '}
            {formatDate(rate.temporary.endDate)}
            {rate.temporary.reason ? ` · ${rate.temporary.reason}` : ''}
          </div>
        )}

        {rate.overlapping && (
          <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 4 }}>
            <StrokeIcon d={Icons.alert} size={13} /> Outra taxa deste tipo cobre parte da mesma faixa de
            parcelas — o legado resolve pela primeira que encontrar.
          </div>
        )}
      </div>

      <div style={{ textAlign: 'right', minWidth: 150 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 17, letterSpacing: '-0.01em' }}>
          {formatRateValue(rate.effectiveValue, rate.rateTypeId)}
        </div>
        {active && (
          <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
            padrão {formatRateValue(rate.standardValue, rate.rateTypeId)}
          </div>
        )}
        {isAwaitingLegacy(rate) && (
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>Aguardando sincronia</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Btn variant="ghost" icon={Icons.history} onClick={onHistory}>
          Histórico
        </Btn>
      </div>

      {canManage && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {hasWindow ? (
            <Btn variant="ghost" icon={Icons.clock} onClick={onEndTemporary}>
              Encerrar temporária
            </Btn>
          ) : (
            <Btn variant="ghost" icon={Icons.clock} onClick={onApplyTemporary}>
              Taxa temporária
            </Btn>
          )}
          <Btn variant="ghost" icon={Icons.pen} onClick={onEdit}>
            Editar
          </Btn>
          <Btn variant="ghost" icon={Icons.x} onClick={onDelete}>
            Excluir
          </Btn>
        </div>
      )}
    </div>
  );
}
